import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ShieldCheck, KeyRound, LogOut, Mail, Phone, Building2, Calendar, ExternalLink } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useClerk } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";

export default function PlatformProfile() {
  const { user, logout } = useAuth();
  const clerk = useClerk();
  const navigate = useNavigate();

  // Password / MFA / active sessions are all owned by Clerk now. Opening
  // the UserProfile modal is the single entry point — it handles password
  // changes, TOTP / passkey enrolment, OAuth account linking, and active
  // session revocation in one place.
  const openClerkProfile = () => clerk.openUserProfile();

  return (
    <div className="space-y-5 max-w-4xl">
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-accent to-primary text-2xl font-bold text-white shadow-glow">
              {user?.avatarInitials ?? "SA"}
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-display text-2xl font-bold">{user?.name}</h2>
                <Badge variant="outline" className="border-accent/40 bg-accent/10 text-accent"><ShieldCheck className="mr-1 h-3 w-3" /> Super Admin</Badge>
              </div>
              <div className="mt-1 text-sm text-muted-foreground">{user?.role} · Access Level: Platform-Wide</div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {user?.email}</span>
                <span className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {user?.phone}</span>
                <span className="flex items-center gap-1.5"><Building2 className="h-3 w-3" /> {user?.department}</span>
                <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Last login: {user?.lastLogin}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Account details</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5"><Label>Full name</Label><Input defaultValue={user?.name} /></div>
          <div className="grid gap-1.5"><Label>Email</Label><Input defaultValue={user?.email} type="email" /></div>
          <div className="grid gap-1.5"><Label>Phone</Label><Input defaultValue={user?.phone} /></div>
          <div className="grid gap-1.5"><Label>Department</Label><Input defaultValue={user?.department} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Security
          </CardTitle>
          <CardDescription>
            Password, two-factor authentication, OAuth providers, and active sessions are all
            managed in your account settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" onClick={openClerkProfile}>
            <ExternalLink className="mr-1.5 h-4 w-4" />
            Manage account & security
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Notification preferences</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {["Critical platform alerts", "New tenant onboarding", "Subscription renewals", "Failed login attempts", "Weekly platform digest"].map(n => (
            <div key={n} className="flex items-center justify-between rounded-lg border p-3">
              <div className="text-sm">{n}</div><Switch defaultChecked />
            </div>
          ))}
        </CardContent>
      </Card>

      <Separator />

      <div className="flex justify-end">
        <Button variant="destructive" onClick={() => { logout(); navigate("/login"); }}>
          <LogOut className="mr-1.5 h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}
